// Short observation built from a criterion's rawData when the API gave none
// (e.g. the AI step failed). It includes the score and explains why it was
// given. French and English versions must cover the same criterion ids.

function getFallbackObservationFr(c) {
  const r = c.rawData;
  if (!r) return `Score : ${c.score}/${c.maxScore}.`;

  const prefix = `Score : ${c.score}/${c.maxScore} — `;

  const map = {
    nap: () =>
      r.name
        ? `${prefix}Nom "${r.name}" détecté. Adresse : ${r.address || "non détectée"}. Téléphone : ${r.phone || "non détecté"}. ${!r.sources?.includes("schema") ? "Pas de source Schema.org, ce qui limite le score." : ""}`
        : `${prefix}Aucune information NAP (nom, adresse, téléphone) détectée sur la page.`,
    h1: () => {
      if (!r.h1s?.length) return `${prefix}Aucun H1 détecté. La balise H1 est essentielle pour le SEO local.`;
      const multiWarning = r.multipleH1s ? ` ⚠️ ${r.h1Count} H1 détectés — il ne devrait y en avoir qu'un seul. Les H1 multiples diluent le signal SEO.` : "";
      const cityInfo = r.cityDetected ? ` Ville "${r.cityDetected}" ${r.cityInH1 ? "présente" : "absente"} dans le H1.` : "";
      return `${prefix}H1 principal : "${r.bestH1 || r.h1s[0]}".${multiWarning}${cityInfo}`;
    },
    title: () => {
      if (!r.title) return `${prefix}Aucune balise title détectée.`;
      const lenNote = r.length > 70 ? " Trop long, risque de troncature dans les SERP." : r.length < 30 ? " Trop court pour être optimisé." : "";
      return `${prefix}Balise title : "${r.title}" (${r.length} caractères).${lenNote}`;
    },
    meta_description: () =>
      r.metaDescription
        ? `${prefix}Meta description : "${r.metaDescription.substring(0, 80)}…" (${r.length} car.).${r.length < 120 ? " Un peu court." : r.length > 160 ? " Risque de troncature." : ""}`
        : `${prefix}Aucune meta description détectée.`,
    schema_org: () =>
      r.hasLocalSchema
        ? `${prefix}Données structurées locales détectées${r.matchedByHeuristic ? " (adresse + horaires)" : ""}${r.type ? `, type ${Array.isArray(r.type) ? r.type.join("/") : r.type}` : ""}. Champs requis : ${r.filledRequired?.join(", ") || "aucun"}. Champs optionnels : ${r.filledOptional?.join(", ") || "aucun"}.`
        : `${prefix}Aucune donnée structurée locale détectée (LocalBusiness ou adresse + horaires). Critère essentiel pour le SEO local.`,
    schema_aggregate_rating: () => {
      if (!r.hasAggregateRating) {
        return `${prefix}Aucun aggregateRating détecté. Ajoutez-le dans le JSON-LD (LocalBusiness ou widget d'avis) avec ratingValue et reviewCount.`;
      }
      const count = r.reviewCount || r.ratingCount;
      const loc =
        r.onLocalSchema === false && r.type
          ? ` dans le JSON-LD (${Array.isArray(r.type) ? r.type.join("/") : r.type})`
          : "";
      if (r.ratingValue && count) return `${prefix}aggregateRating présent${loc} : note ${r.ratingValue}, ${count} avis.`;
      if (r.ratingValue) return `${prefix}aggregateRating partiel${loc} : note ${r.ratingValue} sans nombre d'avis.`;
      return `${prefix}aggregateRating partiel${loc} : ${count} avis sans ratingValue.`;
    },
    schema_same_as: () =>
      r.hasSameAs && r.urls?.length
        ? `${prefix}${r.count || r.urls.length} lien(s) sameAs${r.onLocalSchema === false ? " (hors schéma local)" : ""} : ${r.urls.slice(0, 5).join(", ")}.`
        : `${prefix}Aucun lien sameAs détecté. Ajoutez les URLs de vos fiches officielles (Google Business, réseaux sociaux) dans le JSON-LD.`,
    photos: () =>
      `${prefix}${r.count || 0} images détectées, dont ${r.withAlt || 0} avec attribut alt (${r.altRatio || 0}% de couverture). ${r.descriptiveAlts || 0} alt(s) descriptif(s).`,
    cta: () =>
      r.ctaCount > 0
        ? `${prefix}${r.ctaCount} CTA détecté(s) : ${r.ctaItems?.map((i) => i.text).join(", ")}.`
        : `${prefix}Aucun CTA (appel à l'action) détecté. Les CTA sont essentiels pour convertir les visiteurs.`,
    faq: () =>
      r.hasSchemaFAQ || r.hasHTMLFAQ
        ? `${prefix}Section FAQ détectée (${r.questionCount || 0} questions).${r.hasSchemaFAQ ? " Balisage Schema.org FAQ présent." : " Pas de balisage Schema.org FAQ."}`
        : `${prefix}Aucune section FAQ détectée. Une FAQ améliore le contenu et peut générer des featured snippets.`,
    hours: () =>
      r.hasSchemaHours && r.hasVisibleHours
        ? `${prefix}Horaires détectés dans le Schema.org et visibles sur la page.`
        : r.hasSchemaHours
          ? `${prefix}Horaires trouvés en Schema.org mais pas clairement visibles sur la page.`
          : r.hasVisibleHours
            ? `${prefix}Horaires visibles sur la page mais absents du Schema.org.`
            : `${prefix}Aucun horaire détecté sur la page.`,
    reviews: () => {
      const parts = [];
      if (r.hasRating) parts.push(`note ${r.ratingValue}/5`);
      if (r.reviewCount) parts.push(`${r.reviewCount} avis`);
      if (r.hasCarousel) parts.push("carousel d'avis");
      if (r.hasTestimonials) parts.push("section témoignages");
      if (r.hasWidget) parts.push("widget d'avis");
      return parts.length > 0
        ? `${prefix}Éléments détectés : ${parts.join(", ")}.${!r.hasRating ? " Pas de note agrégée Schema.org détectée." : ""}`
        : `${prefix}Aucun avis ou note détecté. La preuve sociale est un levier important pour la conversion.`;
    },
    social_media: () =>
      r.count > 0
        ? `${prefix}${r.count} réseau(x) social(aux) lié(s) : ${Object.keys(r.platforms || {}).join(", ")}.`
        : `${prefix}Aucun lien vers les réseaux sociaux détecté.`,
    service_links: () =>
      r.count > 0
        ? `${prefix}${r.count} lien(s) vers des pages de services/produits détecté(s).`
        : `${prefix}Aucun lien vers des pages dédiées de services ou produits. Ajoutez des liens vers vos pages de services.`,
    services_products: () =>
      `${prefix}${r.matchCount || 0} pattern(s) de contenu services/produits détecté(s).${r.hasProductSchema ? " Schema.org produit/service présent." : ""}${r.h2Mentions?.length > 0 ? ` H2 mentionnant les services : ${r.h2Mentions.join(", ")}.` : ""}`,
    url: () =>
      r.path
        ? `${prefix}URL : ${r.hostname}${r.path}.`
        : `${prefix}Impossible d'analyser l'URL.`,
    mobile: () =>
      `${prefix}${r.viewport?.exists ? "Balise viewport présente." : "Aucune balise viewport — la page n'est probablement pas responsive."}`,
    certifications: () =>
      r.found?.length > 0
        ? `${prefix}Certifications/labels détectés : ${r.found.join(", ")}.`
        : `${prefix}Aucune certification ou label détecté sur la page.`,
    other_locations: () =>
      r.otherLocationLinks?.length > 0
        ? `${prefix}${r.otherLocationLinks.length} lien(s) vers d'autres établissements détecté(s).`
        : `${prefix}Aucun lien vers d'autres établissements. Le maillage inter-localisations aide le SEO.`,
    internal_linking: () =>
      `${prefix}${r.internalLinkCount || 0} lien(s) interne(s) détecté(s).${(r.internalLinkCount || 0) < 5 ? " Le maillage interne est insuffisant." : ""}`,
    value_proposition: () =>
      `${prefix}${r.matchCount || 0} élément(s) de proposition de valeur détecté(s). Longueur du contenu : ${r.textLength || 0} caractères.`,
    logo: () =>
      r.logo?.found
        ? `${prefix}Logo détecté sur la page.`
        : `${prefix}Aucun logo détecté. Le branding est important pour la reconnaissance.`,
    equipment: () =>
      r.keywords?.length > 0
        ? `${prefix}Équipements détectés : ${r.keywords.join(", ")}.`
        : `${prefix}Aucun équipement ou attribut détecté.`,
    opening_date: () =>
      r.found
        ? `${prefix}Date d'ouverture détectée : ${r.context}.`
        : `${prefix}Aucune date d'ouverture détectée.`,
    map_presence: () =>
      r.hasMap
        ? `${prefix}Carte intégrée détectée (${r.provider || "fournisseur inconnu"}).${r.sources?.length ? ` Source : ${r.sources.join(", ")}.` : ""}`
        : `${prefix}Aucune carte / plan intégré détecté. L'intégration d'une carte (Google Maps, Leaflet, Mapbox…) est essentielle pour une page locale.`,
    maps_link: () =>
      r.count > 0
        ? `${prefix}${r.count} lien(s) vers Google Maps ou directions détecté(s).`
        : `${prefix}Aucun lien vers Google Maps/itinéraire détecté.`,
    promotional_content: () => {
      const items = [];
      if (r.hasGBPPosts) items.push("publications Google Business");
      if (r.hasPosts && !r.hasGBPPosts) items.push("articles/posts");
      if (r.hasBlog) items.push("section blog/actualités");
      if (r.hasPromos) items.push("promotions");
      if (r.hasSocialFeed) items.push("publications sociales");
      if (r.postCount > 0) items.push(`${r.postCount} publication(s)`);
      return items.length > 0
        ? `${prefix}Contenu promotionnel détecté : ${items.join(", ")}.`
        : `${prefix}Aucun contenu promotionnel, article ou publication sociale détecté.`;
    },
  };

  return map[c.id]?.() || `Score : ${c.score}/${c.maxScore}.`;
}

function getFallbackObservationEn(c) {
  const r = c.rawData;
  if (!r) return `Score: ${c.score}/${c.maxScore}.`;

  const prefix = `Score: ${c.score}/${c.maxScore} — `;

  const map = {
    nap: () =>
      r.name
        ? `${prefix}Name "${r.name}" found. Address: ${r.address || "not found"}. Phone: ${r.phone || "not found"}. ${!r.sources?.includes("schema") ? "No Schema.org source, which lowers the score." : ""}`
        : `${prefix}No NAP information (name, address, phone) found on the page.`,
    h1: () => {
      if (!r.h1s?.length) return `${prefix}No H1 found. The H1 tag is essential for local SEO.`;
      const multiWarning = r.multipleH1s ? ` ⚠️ ${r.h1Count} H1 tags found — there should be only one. Multiple H1s dilute the SEO signal.` : "";
      const cityInfo = r.cityDetected ? ` City "${r.cityDetected}" ${r.cityInH1 ? "is" : "is not"} in the H1.` : "";
      return `${prefix}Main H1: "${r.bestH1 || r.h1s[0]}".${multiWarning}${cityInfo}`;
    },
    title: () => {
      if (!r.title) return `${prefix}No title tag found.`;
      const lenNote = r.length > 70 ? " Too long, may be truncated in search results." : r.length < 30 ? " Too short to be well optimized." : "";
      return `${prefix}Title tag: "${r.title}" (${r.length} characters).${lenNote}`;
    },
    meta_description: () =>
      r.metaDescription
        ? `${prefix}Meta description: "${r.metaDescription.substring(0, 80)}…" (${r.length} chars).${r.length < 120 ? " A bit short." : r.length > 160 ? " May be truncated." : ""}`
        : `${prefix}No meta description found.`,
    schema_org: () =>
      r.hasLocalSchema
        ? `${prefix}Local structured data found${r.matchedByHeuristic ? " (address + hours)" : ""}${r.type ? `, type ${Array.isArray(r.type) ? r.type.join("/") : r.type}` : ""}. Required fields: ${r.filledRequired?.join(", ") || "none"}. Optional fields: ${r.filledOptional?.join(", ") || "none"}.`
        : `${prefix}No local structured data found (LocalBusiness or address + hours). This is a key local SEO factor.`,
    schema_aggregate_rating: () => {
      if (!r.hasAggregateRating) {
        return `${prefix}No aggregateRating found. Add it to the JSON-LD (LocalBusiness or review widget) with ratingValue and reviewCount.`;
      }
      const count = r.reviewCount || r.ratingCount;
      const loc =
        r.onLocalSchema === false && r.type
          ? ` in the JSON-LD (${Array.isArray(r.type) ? r.type.join("/") : r.type})`
          : "";
      if (r.ratingValue && count) return `${prefix}aggregateRating found${loc}: rating ${r.ratingValue}, ${count} reviews.`;
      if (r.ratingValue) return `${prefix}Partial aggregateRating${loc}: rating ${r.ratingValue} with no review count.`;
      return `${prefix}Partial aggregateRating${loc}: ${count} reviews with no ratingValue.`;
    },
    schema_same_as: () =>
      r.hasSameAs && r.urls?.length
        ? `${prefix}${r.count || r.urls.length} sameAs link(s)${r.onLocalSchema === false ? " (outside the local schema)" : ""}: ${r.urls.slice(0, 5).join(", ")}.`
        : `${prefix}No sameAs links found. Add the URLs of your official profiles (Google Business Profile, social media) to the JSON-LD.`,
    photos: () =>
      `${prefix}${r.count || 0} images found, ${r.withAlt || 0} with an alt attribute (${r.altRatio || 0}% coverage). ${r.descriptiveAlts || 0} descriptive alt text(s).`,
    cta: () =>
      r.ctaCount > 0
        ? `${prefix}${r.ctaCount} CTA(s) found: ${r.ctaItems?.map((i) => i.text).join(", ")}.`
        : `${prefix}No call to action (CTA) found. CTAs are essential to turn visitors into customers.`,
    faq: () =>
      r.hasSchemaFAQ || r.hasHTMLFAQ
        ? `${prefix}FAQ section found (${r.questionCount || 0} questions).${r.hasSchemaFAQ ? " Schema.org FAQ markup present." : " No Schema.org FAQ markup."}`
        : `${prefix}No FAQ section found. An FAQ adds useful content and can earn featured snippets.`,
    hours: () =>
      r.hasSchemaHours && r.hasVisibleHours
        ? `${prefix}Hours found in Schema.org and visible on the page.`
        : r.hasSchemaHours
          ? `${prefix}Hours found in Schema.org but not clearly visible on the page.`
          : r.hasVisibleHours
            ? `${prefix}Hours visible on the page but missing from Schema.org.`
            : `${prefix}No business hours found on the page.`,
    reviews: () => {
      const parts = [];
      if (r.hasRating) parts.push(`rating ${r.ratingValue}/5`);
      if (r.reviewCount) parts.push(`${r.reviewCount} reviews`);
      if (r.hasCarousel) parts.push("review carousel");
      if (r.hasTestimonials) parts.push("testimonials section");
      if (r.hasWidget) parts.push("review widget");
      return parts.length > 0
        ? `${prefix}Found: ${parts.join(", ")}.${!r.hasRating ? " No Schema.org aggregate rating found." : ""}`
        : `${prefix}No reviews or rating found. Social proof is a strong conversion driver.`;
    },
    social_media: () =>
      r.count > 0
        ? `${prefix}${r.count} social network(s) linked: ${Object.keys(r.platforms || {}).join(", ")}.`
        : `${prefix}No social media links found.`,
    service_links: () =>
      r.count > 0
        ? `${prefix}${r.count} link(s) to service or product pages found.`
        : `${prefix}No links to dedicated service or product pages. Add links to your service pages.`,
    services_products: () =>
      `${prefix}${r.matchCount || 0} service/product content pattern(s) found.${r.hasProductSchema ? " Product/service Schema.org present." : ""}${r.h2Mentions?.length > 0 ? ` H2s mentioning services: ${r.h2Mentions.join(", ")}.` : ""}`,
    url: () =>
      r.path
        ? `${prefix}URL: ${r.hostname}${r.path}.`
        : `${prefix}Could not analyze the URL.`,
    mobile: () =>
      `${prefix}${r.viewport?.exists ? "Viewport tag present." : "No viewport tag — the page is probably not responsive."}`,
    certifications: () =>
      r.found?.length > 0
        ? `${prefix}Certifications/badges found: ${r.found.join(", ")}.`
        : `${prefix}No certifications or badges found on the page.`,
    other_locations: () =>
      r.otherLocationLinks?.length > 0
        ? `${prefix}${r.otherLocationLinks.length} link(s) to other locations found.`
        : `${prefix}No links to other locations. Linking between locations helps local SEO.`,
    internal_linking: () =>
      `${prefix}${r.internalLinkCount || 0} internal link(s) found.${(r.internalLinkCount || 0) < 5 ? " Internal linking is too thin." : ""}`,
    value_proposition: () =>
      `${prefix}${r.matchCount || 0} value proposition element(s) found. Content length: ${r.textLength || 0} characters.`,
    logo: () =>
      r.logo?.found
        ? `${prefix}Logo found on the page.`
        : `${prefix}No logo found. Branding helps visitors recognize you.`,
    equipment: () =>
      r.keywords?.length > 0
        ? `${prefix}Amenities found: ${r.keywords.join(", ")}.`
        : `${prefix}No amenities or attributes found.`,
    opening_date: () =>
      r.found
        ? `${prefix}Opening date found: ${r.context}.`
        : `${prefix}No opening date found.`,
    map_presence: () =>
      r.hasMap
        ? `${prefix}Embedded map found (${r.provider || "unknown provider"}).${r.sources?.length ? ` Source: ${r.sources.join(", ")}.` : ""}`
        : `${prefix}No embedded map found. A map (Google Maps, Leaflet, Mapbox…) is essential on a location page.`,
    maps_link: () =>
      r.count > 0
        ? `${prefix}${r.count} link(s) to Google Maps or directions found.`
        : `${prefix}No link to Google Maps or directions found.`,
    promotional_content: () => {
      const items = [];
      if (r.hasGBPPosts) items.push("Google Business Profile posts");
      if (r.hasPosts && !r.hasGBPPosts) items.push("articles/posts");
      if (r.hasBlog) items.push("blog/news section");
      if (r.hasPromos) items.push("promotions");
      if (r.hasSocialFeed) items.push("social media posts");
      if (r.postCount > 0) items.push(`${r.postCount} post(s)`);
      return items.length > 0
        ? `${prefix}Promotional content found: ${items.join(", ")}.`
        : `${prefix}No promotional content, articles or social posts found.`;
    },
  };

  return map[c.id]?.() || `Score: ${c.score}/${c.maxScore}.`;
}

const getFallbackObservation = (c, lang) =>
  lang === "en" ? getFallbackObservationEn(c) : getFallbackObservationFr(c);

export default getFallbackObservation;
